
import {useApolloClient, WatchQueryFetchPolicy} from '@apollo/client';
import {clearToken, getToken, setToken} from 'utils/auth';
import {dataLayer} from 'utils/window/dataLayer';
import {hsTrack} from 'utils/window/hubspot';
import {impact} from 'utils/window/impact';
import {smartlook} from 'utils/window/smartlook';

import {useAuth} from './query';

export enum Permission {
	Admin = 'Admin',
	Developer = 'Developer',
}

export type Auth = {
	isLoggedIn: boolean;

	isValidUser(): boolean;
	hasPermission(perm: Permission): boolean;
	hasToken(): boolean;
	setToken(token: string | null): Promise<void>;

	user: null | {
		id: string;
		name: string;
		isProvisional: boolean;
	};
}

export const usePermissions = (fetchPolicy: WatchQueryFetchPolicy = 'cache-first') => {
	const {data, error, loading} = useAuth(fetchPolicy);
	const client = useApolloClient();

	// If user set, call identify functions
	if (data?.auth.user) {

		dataLayer.push({userID: data.auth.user.id});
		hsTrack.identify(data.auth.user.details.email);
		smartlook.identify(data.auth.user.id);

		impact.identify({
			id: data.auth.user.id,
			email: data.auth.user.details.email,
		});
	} else {
		impact.identify();
	}

	const auth: Auth = {
		isLoggedIn: Boolean(data?.auth.user),

		isValidUser(): boolean {
			const user = data?.auth.user;
			return Boolean(user && !user.details.isProvisional);
		},

		hasPermission(perm: Permission): boolean {
			if (data?.auth.user?.permissions.all.includes(perm)) {
				return true;
			}
			return false;
		},

		hasToken(): boolean {
			return Boolean(getToken());
		},

		async setToken(token: string | null) {
			if (token) {
				setToken(token);
			} else {
				clearToken();
			}
			await client.resetStore();
		},

		user: !data?.auth.user ? null : {
			id: data.auth.user.id,
			name: data.auth.user.details.firstName,
			isProvisional: data.auth.user.details.isProvisional,
		},
	};

	return {auth, error, loading};
};
